import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  Chip,
  Modal,
  Separator,
  Skeleton,
  Table,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { dt } from "@/shared/helper/datetime";
import QRCode from "react-qr-code";

import { useAdminOrderById } from "@/features/order/use-orders.ts";
import { useFiscalCheckDownload } from "@/features/order/use-fiscal-check.ts";
import {
  Identification,
  OrderStatus,
  OrdersParticipantsTarget,
  Package,
} from "@/features/order/types.ts";
import { orderTools } from "@/shared/helper/order.ts";
import { OrderPriceCard } from "@/features/order/ui/order-price-card.tsx";
import { PageLayout } from "@/shared/components/page-layout.ui.tsx";

export const Route = createFileRoute("/dashboard/order/$orderId/")({
  component: RouteComponent,
});

const statusColorMap: Record<
  OrderStatus,
  "default" | "accent" | "success" | "warning" | "danger"
> = {
  created: "default",
  cancelled: "danger",
  accepted: "accent",
  going_to_sender: "accent",
  decided: "danger",
  submitted: "accent",
  accounted: "warning",
  checked: "warning",
  revision: "warning",
  dispatched: "warning",
  transit: "accent",
  arrived: "accent",
  assigned: "accent",
  delivers_recipient: "accent",
  completed: "success",
};

const participantTargetLabel: Record<OrdersParticipantsTarget, string> = {
  ORDER_INITIATOR: "Отправитель",
  ORDER_RECEIVER: "Получатель",
  ORDER_ACCEPT: "Курьер-отправитель",
  ORDER_REVIEW: "Курьер, принявший заказ",
  ORDER_ACCOUNT: "Оператор на складе",
  ORDER_ASSIGN: "Курьер-получатель",
  ORDER_REVIEW_COURIER: "Курьер",
};

const QR_BASE_URL = "https://l.sapsano.kz/order/identification/";

function PackageQrModal({ pkg }: { pkg: Package }) {
  const [open, setOpen] = useState(false);
  const identification: Identification | undefined = pkg.identifications?.[0];

  if (!identification) return null;

  const qrUrl = `${QR_BASE_URL}${identification.uuid}`;

  return (
    <>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        onPress={() => setOpen(true)}
      >
        <Icon className="size-4" icon="solar:qr-code-outline" />
      </Button>

      <Modal isOpen={open} onOpenChange={setOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center">
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Heading>QR-код посылки — {pkg.name}</Modal.Heading>
              </Modal.Header>
              <Modal.Body className="flex flex-col items-center gap-4 py-4">
                <div className="bg-white p-4 rounded-xl border border-neutral-200">
                  <QRCode size={200} value={qrUrl} />
                </div>
                <div className="w-full flex flex-col gap-1">
                  <span className="text-xs text-neutral-400">Идентификатор</span>
                  <span className="font-mono text-sm break-all select-all bg-neutral-100 rounded px-3 py-2">
                  {identification.uuid}
                </span>
                </div>
                <div className="w-full flex flex-col gap-1">
                  <span className="text-xs text-neutral-400">Ссылка</span>
                  <span className="font-mono text-xs break-all select-all text-neutral-500">
                  {qrUrl}
                </span>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="ghost" onPress={() => setOpen(false)}>
                  Закрыть
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}

function PhotoGrid({ photos }: { photos: string[] }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {photos.map((url, i) => (
        <a
          key={i}
          className="block aspect-square overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100"
          href={url}
          rel="noreferrer"
          target="_blank"
        >
          <img
            alt={`Фото ${i + 1}`}
            className="size-full object-cover"
            loading="lazy"
            src={url}
          />
        </a>
      ))}
    </div>
  );
}

function PackagePhotosModal({ pkg }: { pkg: Package }) {
  const [open, setOpen] = useState(false);

  const photos = pkg.photos ?? [];
  const defectPhotos = pkg.defectPhotos ?? [];
  const total = photos.length + defectPhotos.length;

  if (total === 0) return null;

  return (
    <>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        onPress={() => setOpen(true)}
      >
        <Icon className="size-4" icon="solar:gallery-outline" />
      </Button>

      <Modal isOpen={open} onOpenChange={setOpen}>
        <Modal.Backdrop isDismissable>
          <Modal.Container placement="center">
            <Modal.Dialog>
              <Modal.Header>
                <Modal.Heading>Фотографии посылки — {pkg.name}</Modal.Heading>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-4 py-4">
                {photos.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-neutral-400">Фото посылки</span>
                      <PhotoGrid photos={photos} />
                    </div>
                )}
                {defectPhotos.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <span className="text-xs text-danger-500">Фото дефектов</span>
                      <PhotoGrid photos={defectPhotos} />
                    </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="ghost" onPress={() => setOpen(false)}>
                  Закрыть
                </Button>
              </Modal.Footer>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-neutral-400">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function FiscalCheckButton({ orderId }: { orderId: number }) {
  const download = useFiscalCheckDownload();

  return (
    <Button
      className="ml-auto"
      isDisabled={download.isPending}
      size="sm"
      variant="ghost"
      onPress={() => download.mutate({ orderId })}
    >
      <Icon className="size-4" icon="solar:document-text-outline" />
      {download.isPending ? "Готовим чек…" : "Фискальный чек"}
    </Button>
  );
}

function RouteComponent() {
  const { orderId } = Route.useParams();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useAdminOrderById({ orderId });
  const order = data?.data;

  const formattedId = order ? orderTools.formatOrderId(order.id) : orderId;

  return (
    <PageLayout
      title={
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            onPress={() => navigate({ to: "/dashboard/orders" })}
          >
            <Icon className="size-5" icon="solar:arrow-left-outline" />
          </Button>
          <span>Заказ {formattedId}</span>
          {order && (
            <Chip
              color={statusColorMap[order.status] ?? "default"}
              size="sm"
              variant="soft"
            >
              {orderTools.status.toString(order.status)}
            </Chip>
          )}
          {order && <FiscalCheckButton orderId={order.id} />}
        </div>
      }
    >
      {isError && (
        <div className="flex flex-col items-center justify-center py-16 gap-2 text-neutral-400">
          <Icon className="size-10" icon="solar:danger-circle-outline" />
          <p>Заказ не найден</p>
        </div>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-5 w-32 rounded mb-4" />
              <div className="flex flex-col gap-3">
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-3/4 rounded" />
                <Skeleton className="h-4 w-1/2 rounded" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {order && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          {/* FROM */}
          <Card>
            <Card.Header className="pb-0 flex items-center gap-2">
              <Icon
                className="size-4 text-neutral-400"
                icon="solar:map-point-outline"
              />
              <span className="font-semibold text-sm">Откуда</span>
            </Card.Header>
            <Separator className="mt-2" />
            <Card.Content className="flex flex-col gap-3">
              <InfoRow label="Город" value={order.fromCity.name} />
              <InfoRow label="Адрес" value={order.fromAddress} />
              {order.fromApartment && (
                <InfoRow label="Квартира" value={order.fromApartment} />
              )}
              {order.fromEntrance && (
                <InfoRow label="Подъезд" value={order.fromEntrance} />
              )}
              {order.fromFloor && (
                <InfoRow label="Этаж" value={order.fromFloor} />
              )}
              <InfoRow label="Контакт" value={order.fromName} />
              <InfoRow label="Телефон" value={order.fromPhone} />
            </Card.Content>
          </Card>

          {/* TO */}
          <Card>
            <Card.Header className="pb-0 flex items-center gap-2">
              <Icon
                className="size-4 text-neutral-400"
                icon="solar:map-point-wave-outline"
              />
              <span className="font-semibold text-sm">Куда</span>
            </Card.Header>
            <Separator className="mt-2" />
            <Card.Content className="flex flex-col gap-3">
              <InfoRow label="Город" value={order.toCity.name} />
              <InfoRow label="Адрес" value={order.toAddress} />
              {order.toApartment && (
                <InfoRow label="Квартира" value={order.toApartment} />
              )}
              {order.toEntrance && (
                <InfoRow label="Подъезд" value={order.toEntrance} />
              )}
              {order.toFloor && <InfoRow label="Этаж" value={order.toFloor} />}
              <InfoRow label="Контакт" value={order.toName} />
              <InfoRow label="Телефон" value={order.toPhone} />
            </Card.Content>
          </Card>

          {/* ORDER INFO */}
          <Card>
            <Card.Header className="pb-0 flex items-center gap-2">
              <Icon
                className="size-4 text-neutral-400"
                icon="solar:clipboard-list-outline"
              />
              <span className="font-semibold text-sm">Информация о заказе</span>
            </Card.Header>
            <Separator className="mt-2" />
            <Card.Content className="flex flex-col gap-3">
              <InfoRow
                label="Дата создания"
                value={dt(order.createdAt).format("DD.MM.YYYY HH:mm")}
              />
              <InfoRow
                label="Дата обновления"
                value={dt(order.updatedAt).format("DD.MM.YYYY HH:mm")}
              />
              {order.pickupSlotStart && (
                <InfoRow
                  label="Слот забора"
                  value={dt(order.pickupSlotStart).format(
                    "DD.MM.YYYY HH:mm",
                  )}
                />
              )}
              {order.pickUpTime && (
                <InfoRow
                  label="Время забора"
                  value={dt(order.pickUpTime).format("DD.MM.YYYY HH:mm")}
                />
              )}
              {order.expectedArrival && (
                <InfoRow
                  label="Ожидаемое прибытие"
                  value={dt(order.expectedArrival).format(
                    "DD.MM.YYYY HH:mm",
                  )}
                />
              )}
              {order.description && (
                <InfoRow label="Описание" value={order.description} />
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-neutral-400">
                  Оплата получателем
                </span>
                <Chip
                  color={order.isPayReceiver ? "warning" : "default"}
                  size="sm"
                  variant="soft"
                >
                  {order.isPayReceiver ? "Да" : "Нет"}
                </Chip>
              </div>
              {order.insuranceValue > 0 && (
                <InfoRow
                  label="Страховая стоимость"
                  value={`${order.insuranceValue.toLocaleString("ru-RU")} ₸`}
                />
              )}
              {order.insuranceFee > 0 && (
                <InfoRow
                  label="Страховой взнос"
                  value={`${order.insuranceFee.toLocaleString("ru-RU")} ₸`}
                />
              )}
              {order.orderAccumulatorId && (
                <InfoRow
                  label="ID аккумулятора"
                  value={String(order.orderAccumulatorId)}
                />
              )}
            </Card.Content>
          </Card>

          {/* PARTICIPANTS */}
          {order.fullParticipants.length > 0 && (
            <Card>
              <Card.Header className="pb-0 flex items-center gap-2">
                <Icon
                  className="size-4 text-neutral-400"
                  icon="solar:users-group-rounded-outline"
                />
                <span className="font-semibold text-sm">Участники</span>
              </Card.Header>
              <Separator className="mt-2" />
              <Card.Content className="flex flex-col gap-2">
                {order.fullParticipants.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between py-1"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{p.userName}</span>
                      <span className="text-xs text-neutral-400">
                        {participantTargetLabel[p.target] ?? p.target}
                      </span>
                    </div>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}

          {/* PACKAGES */}
          {order.packages.length > 0 && (
            <Card className="lg:col-span-2">
              <Card.Header className="pb-0 flex items-center gap-2">
                <Icon
                  className="size-4 text-neutral-400"
                  icon="solar:box-outline"
                />
                <span className="font-semibold text-sm">Посылки</span>
              </Card.Header>
              <Separator className="mt-2" />
              <Card.Content className="p-0">
                <Table.Root>
                  <Table.Content aria-label="Посылки" className="min-w-full">
                    <Table.Header>
                      <Table.Column>Название</Table.Column>
                      <Table.Column>Тариф</Table.Column>
                      <Table.Column>Вес, кг</Table.Column>
                      <Table.Column>Габариты (Д×Ш×В), см</Table.Column>
                      <Table.Column>Объёмный вес</Table.Column>
                      <Table.Column>Хрупкий</Table.Column>
                      <Table.Column>Цена</Table.Column>
                      <Table.Column>Фото</Table.Column>
                      <Table.Column>QR</Table.Column>
                    </Table.Header>
                    <Table.Body items={order.packages}>
                      {(pkg) => (
                        <Table.Row id={String(pkg.id)}>
                          <Table.Cell className="text-sm">
                            {pkg.name}
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {pkg.tariff?.name ?? "—"}
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {pkg.weight}
                          </Table.Cell>
                          <Table.Cell className="text-sm font-mono">
                            {pkg.length}×{pkg.width}×{pkg.height}
                          </Table.Cell>
                          <Table.Cell className="text-sm">
                            {pkg.volumetricWeight}
                          </Table.Cell>
                          <Table.Cell>
                            <Chip
                              color={pkg.isFragile ? "warning" : "default"}
                              size="sm"
                              variant="soft"
                            >
                              {pkg.isFragile ? "Да" : "Нет"}
                            </Chip>
                          </Table.Cell>
                          <Table.Cell className="text-sm font-medium">
                            {pkg.sellingPrice.toLocaleString("ru-RU")} ₸
                          </Table.Cell>
                          <Table.Cell>
                            <PackagePhotosModal pkg={pkg} />
                          </Table.Cell>
                          <Table.Cell>
                            <PackageQrModal pkg={pkg} />
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table.Content>
                </Table.Root>
              </Card.Content>
            </Card>
          )}

          <OrderPriceCard
            canAdjust={
              order.status !== "completed" && order.status !== "cancelled"
            }
            orderId={order.id}
          />

          {order.histories.length > 0 && (
            <Card className="lg:col-span-2">
              <Card.Header className="pb-0 flex items-center gap-2">
                <Icon
                  className="size-4 text-neutral-400"
                  icon="solar:history-outline"
                />
                <span className="font-semibold text-sm">История статусов</span>
              </Card.Header>
              <Separator className="mt-2" />
              <Card.Content className="flex flex-col gap-2">
                {order.histories.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between py-1 border-b border-neutral-100 last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <Chip
                        color={statusColorMap[h.status] ?? "default"}
                        size="sm"
                        variant="soft"
                      >
                        {orderTools.status.toString(h.status)}
                      </Chip>
                      {h.description && (
                        <span className="text-xs text-neutral-500">
                          {h.description}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-neutral-400">
                      {dt(h.createdAt).format("DD.MM.YYYY HH:mm")}
                    </span>
                  </div>
                ))}
              </Card.Content>
            </Card>
          )}
        </div>
      )}
    </PageLayout>
  );
}
